import moment from "moment";
import { getExpensesByLevels, getExpensesSummaryCards } from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";
import { resolveDateRange, buildHeadersByPeriod, formatPeriodLabel } from "./utils/dateRange";

// En vistas year/ytd mostramos varios años hacia atrás, igual que ResultadosTab/VentasTab.
const YEARS_SHOWED = 4;

// Cuentas level_2 cuya suma forma el numerador del ratio "% Gastos / Ventas".
// Coincide con el cálculo de `gastos/ventas_%` en ResultadosTab (ver
// reportStructure.js: keysSum = ['gastos_administracion', 'gastos_comerciales',
// 'gastos_i+d']), excluyendo `gastos_no_operacional` a propósito.
const EFFICIENCY_L2_ACCOUNTS = [
    "Gastos De Administración",
    "Gastos Comerciales",
    "Gastos I+D",
];

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const state = () => ({
    selectedCounterpartTypes: [],
    monthHeaders: [],
    accountsData: {},
    level2Accounts: [],
    level3Accounts: [],
    hierarchy: {},
    /** Total de ventas netas por etiqueta de período, ej. { "Ene 2025": 1234567, ... } */
    salesByPeriod: {},
    periodRange: { init_date: null, end_date: null },
    isLoading: false,
    error: null,
    /**
     * Resumen para las summary cards: { current_month, ytd, ltm }.
     * Cada entrada incluye period_start, period_end, amount y ratios mom/yoy.
     * Se calcula independiente de los filtros de la tabla porque necesita
     * comparables que pueden quedar fuera del rango visible (LTM, YoY, etc.).
     */
    summaryCards: null,
    isSummaryLoading: false,
    summaryError: null,
});

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------

const getters = {
    isCuentasMode: (state) => state.selectedCounterpartTypes.length === 0,

    groupByParam: (state) => {
        if (state.selectedCounterpartTypes.length === 0) return "cuentas";
        return state.selectedCounterpartTypes.join(",");
    },

    tableRows: (state) => {
        const rows = [];

        state.level2Accounts.forEach(l2 => {
            // Fila de Nivel 2
            rows.push({
                label: l2,
                level: 2,
                isTotal: false,
                values: state.monthHeaders.map(
                    (header) => state.accountsData[l2]?.[header] ?? 0
                ),
            });

            // Filas de Nivel 3 para este Nivel 2
            const children = state.hierarchy[l2] || [];
            children.forEach(l3 => {
                // Evitar duplicados si el nombre es igual al nivel 2
                if (l3 === l2) return;

                rows.push({
                    label: l3,
                    level: 3,
                    parent: l2,
                    isTotal: false,
                    values: state.monthHeaders.map(
                        (header) => state.accountsData[l3]?.[header] ?? 0
                    ),
                });

            });
        });

        return rows;
    },

    totalRow: (state) => {
        const values = state.monthHeaders.map((header) => {
            return state.level2Accounts.reduce((sum, account) => {
                const val = Number(state.accountsData[account]?.[header] || 0);
                return sum + (isNaN(val) ? 0 : val);
            }, 0);
        });

        return { label: "Total Gastos", values, isTotal: true };
    },

    /**
     * Ratio gastos operacionales / ventas netas por período. Numerador =
     * suma de las level_2 en EFFICIENCY_L2_ACCOUNTS (no usa totalRow porque
     * ese podría incluir 'Gastos No Operacionales', inconsistente con el
     * ratio mostrado en Estado de Resultados). Valores como ratio decimal
     * (0.12 = 12%); se formatea en la UI con formatPercent.
     */
    ratioRow: (state) => {
        const values = state.monthHeaders.map((header) => {
            const gastos = EFFICIENCY_L2_ACCOUNTS.reduce((sum, account) => {
                const val = Number(state.accountsData[account]?.[header] || 0);
                return sum + (isNaN(val) ? 0 : val);
            }, 0);
            const ventas = Number(state.salesByPeriod[header] || 0);
            return ventas !== 0 ? gastos / ventas : 0;
        });
        return { label: "% Gastos / Ventas", values, isRatio: true };
    },
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const mutations = {
    SET_SELECTED_COUNTERPART_TYPES(state, types) {
        state.selectedCounterpartTypes = Array.isArray(types) ? [...types] : [];
    },
    SET_EXPENSES_DATA(state, { level2Accounts, level3Accounts, hierarchy, accountsData, monthHeaders, periodRange, salesByPeriod }) {
        state.level2Accounts = level2Accounts;
        state.level3Accounts = level3Accounts;
        state.hierarchy = hierarchy;
        state.accountsData = accountsData;
        state.monthHeaders = monthHeaders;
        state.periodRange = periodRange;
        state.salesByPeriod = salesByPeriod || {};
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_SUMMARY_CARDS(state, summary) {
        state.summaryCards = summary;
    },
    SET_SUMMARY_LOADING(state, isLoading) {
        state.isSummaryLoading = isLoading;
    },
    SET_SUMMARY_ERROR(state, error) {
        state.summaryError = error;
    },
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

const actions = {
    async fetchExpensesData({ getters, commit, rootState }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const endMonth = rootState.reportsFilters.endMonth;
            const filterPeriod = rootState.financialReports.filterPeriod;

            let { initDate, endDate } = resolveDateRange(
                endMonth,
                rootState.reportsFilters.selectedPeriodRange
            );

            // En year/ytd extendemos el rango hacia atrás para mostrar varios
            // años como columnas, replicando el comportamiento de ResultadosTab.
            if (filterPeriod === "year" || filterPeriod === "ytd") {
                initDate = moment(endMonth)
                    .subtract(YEARS_SHOWED, "year")
                    .startOf("year")
                    .format("YYYY-MM-DD");
            }

            const response = await getExpensesByLevels(initDate, endDate, getters.groupByParam, filterPeriod);
            const {
                report_data,
                level2_accounts,
                level3_accounts,
                hierarchy,
                sales_by_period,
            } = response.data;

            // Cabeceras según granularidad (mes/trimestre/año).
            const monthHeaders = buildHeadersByPeriod(initDate, endDate, filterPeriod);
            const accountsData = {};

            // El backend ya devuelve una fila por bucket truncado (DATE_TRUNC)
            // y aplica el cutoff YTD vía ytd_month_limit; acá solo formateamos.
            Object.keys(report_data).forEach(accountName => {
                accountsData[accountName] = {};
                report_data[accountName].forEach(row => {
                    const header = formatPeriodLabel(row.period, filterPeriod);
                    const amount = Number(row.total_amount);
                    accountsData[accountName][header] = isNaN(amount) ? 0 : amount;
                });

            });

            // Total de ventas netas por etiqueta de período → input al ratio
            // "% Gastos / Ventas". Misma normalización de label que accountsData.
            const salesByPeriod = {};
            (sales_by_period || []).forEach(row => {
                const header = formatPeriodLabel(row.period, filterPeriod);
                const amount = Number(row.total_amount);
                salesByPeriod[header] = isNaN(amount) ? 0 : amount;
            });

            commit("SET_EXPENSES_DATA", {
                level2Accounts: level2_accounts,
                level3Accounts: level3_accounts,
                hierarchy,
                accountsData,
                monthHeaders,
                periodRange: { init_date: initDate, end_date: endDate },
                salesByPeriod,
            });
        } catch (error) {
            consoleHandlerError({ error, prefix: "[expensesReport]", silent: true });
            commit("SET_ERROR", "Error al cargar gastos.");
        } finally {
            commit("SET_LOADING", false);
        }
    },

    async fetchExpensesSummary({ commit, rootState }) {
        commit("SET_SUMMARY_LOADING", true);
        commit("SET_SUMMARY_ERROR", null);

        try {
            const endDate = moment(rootState.reportsFilters.endMonth)
                .endOf("month")
                .format("YYYY-MM-DD");

            const response = await getExpensesSummaryCards(endDate);
            commit("SET_SUMMARY_CARDS", Object.freeze(response.data));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[expensesReport]", silent: true });
            commit(
                "SET_SUMMARY_ERROR",
                extractErrorMessage(error, "Error al cargar resumen de gastos.")
            );
        } finally {
            commit("SET_SUMMARY_LOADING", false);
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
